import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api, ApiError } from "@/api/client";
import { Button, FormField } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";

interface ReversalPreviewDto {
  originalPaymentId: string;
  impactedPaymentIds: string[];
  directDisbursementCount: number;
  affectedDisbursementCount: number;
  instructionIssuedCount: number;
  warnings: string[];
}

interface ReversalResultDto {
  reversedPaymentId: string;
  recomputedDownstreamPaymentIds: string[];
  reversedDisbursementCount: number;
  newDisbursementCount: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  paymentId: string;
  paymentLabel?: string;
  /** SP-2 Monthly Cycle Cockpit — pre-seeds the reason textarea (editable). */
  initialReason?: string;
  onReversed?: (result: ReversalResultDto) => void;
}

export function ReversalConfirmationModal({
  open,
  onClose,
  paymentId,
  paymentLabel,
  initialReason,
  onReversed,
}: Props) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sunset, setSunset] = useState(false);

  useEffect(() => {
    if (open) {
      setReason(initialReason ?? "");
      setError(null);
      setSunset(false);
    }
  }, [open, paymentId, initialReason]);

  const preview = useQuery({
    queryKey: ["payment-reversal-preview", paymentId],
    queryFn: () => api.post<ReversalPreviewDto>(`/payments/${paymentId}/reverse/preview`, {}),
    enabled: open,
    retry: false,
    gcTime: 0,
  });

  useEffect(() => {
    if (preview.error instanceof ApiError && preview.error.status === 422) {
      setSunset(true);
    }
  }, [preview.error]);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<ReversalResultDto>(`/payments/${paymentId}/reverse`, { reason: reason.trim() }),
    onSuccess: (result) => {
      onReversed?.(result);
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setSunset(true);
        return;
      }
      setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e));
    },
  });

  const canSubmit = reason.trim().length > 0 && !preview.isLoading && !sunset;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Reverse Payment ${paymentLabel ?? paymentId}`}
      description="Reversal posts a negative offsetting payment and recomputes downstream priorities."
      size="lg"
      footer={
        sunset ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            {/* TODO: Parent should switch to AdjustmentWizard when this fires. */}
            <Button variant="primary" onClick={onClose}>
              Go to Adjustment
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={mutation.isPending}
              disabled={!canSubmit}
              onClick={() => {
                setError(null);
                mutation.mutate();
              }}
            >
              Confirm reversal
            </Button>
          </>
        )
      }
    >
      {sunset && (
        <div className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>This payment has been instructed; use Adjustment Entry instead.</span>
        </div>
      )}

      {!sunset && preview.isLoading && (
        <div className="flex items-center gap-space-2 text-gray-mid text-small p-space-4">
          <Loader2 size={16} className="animate-spin" aria-hidden />
          <span>Loading reversal impact…</span>
        </div>
      )}

      {!sunset && preview.error && !(preview.error instanceof ApiError && preview.error.status === 422) && (
        <div className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>
            {preview.error instanceof ApiError
              ? (preview.error.errors[0] ?? preview.error.message)
              : String(preview.error)}
          </span>
        </div>
      )}

      {!sunset && preview.data && (
        <div className="flex flex-col gap-space-4">
          {error && (
            <div className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small">
              <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
              <span>{error}</span>
            </div>
          )}

          <section>
            <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
              Downstream impact
            </div>
            <p className="text-small text-onyx">
              {preview.data.impactedPaymentIds.length} downstream payments will be recomputed.
            </p>
            {preview.data.impactedPaymentIds.length > 0 && (
              <ul className="mt-space-2 border border-border-light rounded-md divide-y divide-border-light bg-white-pure max-h-48 overflow-y-auto">
                {preview.data.impactedPaymentIds.map((id) => (
                  <li key={id} className="px-space-3 py-space-2 text-small font-mono text-onyx">
                    {id}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
              Affected disbursements
            </div>
            <p className="text-small text-onyx">
              {preview.data.affectedDisbursementCount} disbursements ({preview.data.directDisbursementCount} direct).
            </p>
          </section>

          {preview.data.warnings.length > 0 && (
            <div className="flex items-start gap-space-2 p-space-3 border border-status-warning rounded-md bg-status-warning/10 text-status-warning text-small">
              <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
              <ul className="list-disc list-inside">
                {preview.data.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <FormField
            label="Reason for reversal"
            required
            helperText="Recorded in the immutable audit trail."
          >
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
            />
          </FormField>
        </div>
      )}
    </Modal>
  );
}
